import { SERVER_TICK_MS } from '../constants.js';
import { findPath, isAdjacentStep } from '../world/pathfinding.js';
/**
 * The rules the server applies to movement.
 *
 * A client sends a destination and predicts its own walk; the server replays
 * the same path at the same speed and is the only authority on where a player
 * actually is (CLAUDE.md rule 1). Everything here is pure so both sides run it
 * identically and the tests can pin the exact limits.
 */
/** Anything that walks declares a speed; classes and mobs share the pacing rule. */
export function msPerTile(definition, speedMultiplier = 1) {
    return 1000 / (definition.tilesPerSecond * speedMultiplier);
}
/**
 * Milliseconds between two steps as the server walks them, on average.
 *
 * The server only moves a player on its ticks, but the step clock advances by
 * this exact interval (`nextStepClock`), so a pace that is not a whole number
 * of ticks alternates between the two nearest (4.4 tiles a second: 250 then
 * 200 ms) and keeps its true average. Rounding it up to a whole tick, as it
 * once did, swallowed every bonus under a fifth: boots worth a few percent
 * (ADR 0059) would have changed nothing, and a ×1.6 horse walked at ×1.25.
 * Never under a tick: no walker takes two steps in one. The client predicts
 * its own walk with this very number (ADR 0045).
 */
export function stepIntervalMs(definition, speedMultiplier = 1, tickMs = SERVER_TICK_MS) {
    return Math.max(tickMs, msPerTile(definition, speedMultiplier));
}
/**
 * Whether a walker that entered its tile `elapsedMs` ago may take the next step.
 *
 * Ticks come from a wall-clock timer and land a few milliseconds early or late;
 * comparing to the exact interval would push a step whose tick came 1 ms early
 * to the tick after, and a warrior would walk at 300 ms a tile now and then. Half
 * a tick of slack keeps the cadence to a whole number of ticks, the one the
 * client predicts.
 */
export function isStepDue(elapsedMs, intervalMs, tickMs = SERVER_TICK_MS) {
    return elapsedMs >= intervalMs - tickMs / 2;
}
/**
 * The step clock of a walker that starts a walk while standing: its first
 * step falls due at once, the next one an interval later. Mid-step, the clock
 * is kept and the new path waits for the tile being crossed. The client does
 * the same when it predicts the walk (ADR 0045).
 */
export function walkStartClock(lastStepAt, now, intervalMs) {
    return Math.max(lastStepAt, now - intervalMs);
}
/**
 * The step clock after a step taken at `now`.
 *
 * It moves by exactly one interval, not to `now`: a tick that fires late (a
 * slow tick before it, or the fixed-timestep catch-up that runs several steps
 * at the same clock) would otherwise push every later step back, and the
 * server would fall a little further behind the client's prediction at each
 * hitch. The walker may owe one step at most, so a long freeze is followed by
 * one quick step, never by a sprint.
 */
export function nextStepClock(lastStepAt, now, intervalMs) {
    return Math.max(lastStepAt + intervalMs, now - intervalMs);
}
/**
 * Slack allowed on step timing, as a fraction of one tile.
 *
 * Network jitter makes a client's steps arrive slightly early; rejecting those
 * would stutter honest players. A quarter tile absorbs jitter while still
 * catching a speed hack, which has to sustain a much larger gain to matter.
 */
export const STEP_TOLERANCE = 0.25;
/** True when enough time has passed for the player to legally enter the next tile. */
export function canStepNow({ lastStepAt, now, definition }) {
    const required = msPerTile(definition) * (1 - STEP_TOLERANCE);
    return now - lastStepAt >= required;
}
export const DEFAULT_MAX_PATH_LENGTH = 64;
/**
 * How many tiles A* may expand to find a path of at most `length` steps: every
 * tile it looks at lies within that Manhattan distance of the start (a
 * diamond), so the bound never refuses a path that fits, and an unreachable
 * click no longer sweeps the whole 384×384 world.
 */
export function pathSearchBudget(length) {
    return 2 * length * (length + 1) + 1;
}
/**
 * Validate a `move_to` and return the path the server will walk.
 *
 * Rejects rather than clamps: a request the server cannot honour exactly is a
 * bug or an attack, and silently walking somewhere else would desync the client.
 */
export function validateMoveRequest(grid, from, to, options = {}) {
    if (from.x === to.x && from.y === to.y)
        return { ok: false, reason: 'same-tile' };
    if (!grid.isWalkableTile(to))
        return { ok: false, reason: 'unwalkable-target' };
    const maxPathLength = options.maxPathLength ?? DEFAULT_MAX_PATH_LENGTH;
    if (Math.abs(to.x - from.x) + Math.abs(to.y - from.y) > maxPathLength) {
        return { ok: false, reason: 'path-too-long' };
    }
    const path = findPath(grid, from, to, { maxExpanded: pathSearchBudget(maxPathLength) });
    if (path === null)
        return { ok: false, reason: 'no-path' };
    if (path.length > maxPathLength + 1)
        return { ok: false, reason: 'path-too-long' };
    return { ok: true, path };
}
/**
 * Validate a `move_to` sent while a walk is under way, from the tile the
 * client's predicted walk turns on (ADR 0045).
 *
 * The client walks ahead of the server, so on a click mid-walk the two stand
 * on different tiles. Searching from each would let A* pick two paths of the
 * same length, and the client would be pulled back onto the server's. So the
 * server turns where the client did:
 *
 * - `from` is still ahead on its path (the usual case): it finishes its path
 *   up to it, then takes the path A* finds from there, the client's own;
 * - `from` is the tile it just left (`justLeft`): the click raced its last
 *   step, through network jitter or the half tick of `isStepDue`. It steps
 *   back onto it and takes the same path, or carries on if that path runs
 *   through the tile it stands on;
 * - otherwise (no hint, or a hint the server never walked) it searches from
 *   its own tile.
 *
 * `from` never takes the player anywhere it was not about to go or had just
 * been: a hint, not a position.
 */
export function validateMoveFrom(grid, at, remaining, from, to, justLeft, options = {}) {
    const own = () => validateMoveRequest(grid, at, to, options);
    if (from === undefined || samePlace(from, at))
        return own();
    const turnAt = remaining.findIndex((tile) => samePlace(tile, from));
    const behind = justLeft !== undefined && samePlace(justLeft, from);
    if (turnAt < 0 && !behind)
        return own();
    const turn = validateMoveRequest(grid, from, to, options);
    if (!turn.ok || turn.path === undefined)
        return own();
    const after = turn.path.slice(1);
    const start = { x: at.x, y: at.y };
    if (turnAt >= 0)
        return { ok: true, path: [start, ...remaining.slice(0, turnAt + 1), ...after] };
    const through = after[0] !== undefined && samePlace(after[0], at);
    return { ok: true, path: through ? [start, ...after.slice(1)] : [start, from, ...after] };
}
function samePlace(a, b) {
    return a.x === b.x && a.y === b.y;
}
/**
 * Validate one tile of movement: adjacent, walkable, and not too soon.
 *
 * This is the check that actually stops a speed hack, because it runs on every
 * tile a player enters rather than once per click.
 */
export function validateStep(grid, from, to, timing) {
    if (!isAdjacentStep(from, to))
        return { ok: false, reason: 'not-adjacent' };
    if (!grid.isWalkableTile(to))
        return { ok: false, reason: 'unwalkable-target' };
    if (!canStepNow(timing))
        return { ok: false, reason: 'too-fast' };
    return { ok: true, path: [from, to] };
}
export const DIRECTIONS_BY_DELTA = [
    { dx: 0, dy: -1, direction: 'north' },
    { dx: 1, dy: 0, direction: 'east' },
    { dx: 0, dy: 1, direction: 'south' },
    { dx: -1, dy: 0, direction: 'west' },
];
/**
 * Facing implied by a step. Returns null for anything that is not a single
 * orthogonal move, so callers keep the previous facing rather than snapping.
 */
export function facingFromStep(from, to) {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    return DIRECTIONS_BY_DELTA.find((entry) => entry.dx === dx && entry.dy === dy)?.direction ?? null;
}
/**
 * Facing toward a tile at any distance, along the larger axis. A diagonal
 * neighbour faces sideways: the side poses read better than a back turned
 * half away. Null on the same tile.
 */
export function facingToward(from, to) {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    if (dx === 0 && dy === 0)
        return null;
    if (Math.abs(dx) >= Math.abs(dy))
        return dx > 0 ? 'east' : 'west';
    return dy > 0 ? 'south' : 'north';
}
//# sourceMappingURL=movement.js.map